import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import {
 Dialog,
 DialogContent,
 DialogHeader,
 DialogTitle,
 DialogFooter,
} from '../../../components/ui/Dialog';
import { FormField } from '../../../components/ui/FormField';
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog';
import { OrgUnitPicker } from '../../org/components/OrgUnitPicker';
import { useOrgUnits } from '../../org/network/org.api';
import { agentHooks } from '../hooks';
import { useToast } from '../../../components/ui/Toast';

interface AgentTerritoryTabProps {
 agentId: string;
}

export function AgentTerritoryTab({ agentId }: AgentTerritoryTabProps) {
 const [assignOpen, setAssignOpen] = useState(false);
 // Using single select for MVP, though spec mentions multi-select is possible.
 // OrgUnitPicker handles one at a time right now. We can assign them sequentially or update Picker.
 const [selectedOrgUnitId, setSelectedOrgUnitId] = useState('');
 const [removeTarget, setRemoveTarget] = useState<{ orgUnitId: string; isPrimary: boolean } | null>(null);

 const { data: territoryResponse, isLoading } = agentHooks.useTerritory(agentId);
 const { data: orgUnitsResponse } = useOrgUnits();

 const assignMutation = agentHooks.useAssignTerritory();
 const removeMutation = agentHooks.useRemoveTerritory();
 const { toast } = useToast();

 const territories = territoryResponse?.data || [];
 const assignedOrgUnitIds = new Set(territories.map(t => t.orgUnitId));
 
 // Filter out already assigned units from the picker options
 // By flattening, or we could just rely on users not selecting duplicates.
 // OrgUnitPicker accepts full node list, we don't have a way to disable individual nodes natively without modifying it, 
 // but it's fine for now, backend will reject or ignore duplicates.

 const handleAssign = async () => {
 if (!selectedOrgUnitId) return;
 try {
 await assignMutation.mutateAsync({ id: agentId, body: { orgUnitIds: [selectedOrgUnitId] } });
 setAssignOpen(false);
 setSelectedOrgUnitId('');
 toast({ title: 'Territory Assigned', type: 'success' });
 } catch (e: any) {
 console.error('Failed to assign territory', e);
 toast({ title: 'Assignment Failed', description: e.message, type: 'error' });
 }
 };

 const removeTerritory = async (orgUnitId: string) => {
 try {
 await removeMutation.mutateAsync({ id: agentId, orgUnitId });
 toast({ title: 'Territory Removed', type: 'success' });
 } catch (e: any) {
 console.error('Failed to remove territory', e);
 toast({ title: 'Removal Failed', description: e.message, type: 'error' });
 }
 };

 const handleRemove = (orgUnitId: string, isPrimary: boolean) => {
 if (territories.length === 1) {
 toast({
 title: 'Cannot remove territory',
 description: 'An agent must have at least one territory assigned.',
 type: 'error',
 });
 return;
 }

 if (isPrimary) {
 setRemoveTarget({ orgUnitId, isPrimary });
 return;
 }

 void removeTerritory(orgUnitId);
 };

 if (isLoading) {
 return <div className="py-8 text-center text-text-muted">Loading territories...</div>;
 }

 return (
 <div className="flex flex-col gap-6">
 <div className="flex justify-between items-center">
 <h3 className="text-lg font-medium text-text-primary">Assigned Territories</h3>
 <Button onClick={() => setAssignOpen(true)} variant="secondary" size="sm">
 <Plus className="mr-2 h-4 w-4" />
 Assign Territory
 </Button>
 </div>

 <div className="border border-border rounded-md overflow-hidden bg-surface-1">
 <table className="w-full text-sm text-left">
 <thead className="bg-surface-2 text-text-secondary uppercase text-xs">
 <tr>
 <th className="px-4 py-3 font-medium">Branch / Territory</th>
 <th className="px-4 py-3 font-medium">Status</th>
 <th className="px-4 py-3 font-medium">Assigned At</th>
 <th className="px-4 py-3 text-right font-medium">Actions</th>
 </tr>
 </thead>
 <tbody className="divide-y divide-border">
 {territories.length === 0 ? (
 <tr>
 <td colSpan={4} className="px-4 py-8 text-center text-text-muted">
 No territories assigned.
 </td>
 </tr>
 ) : (
 territories.map((t) => (
 <tr key={t.id} className="hover:bg-surface-2/50">
 <td className="px-4 py-3 font-medium text-text-primary">
 {t.orgUnitName || t.orgUnitId}
 </td>
 <td className="px-4 py-3">
 {t.isPrimary ? (
 <span className="bg-accent-500/10 text-accent-500 px-2 py-0.5 rounded text-caption uppercase font-medium">
 Primary
 </span>
 ) : (
 <span className="text-text-muted text-caption uppercase font-medium">
 Secondary
 </span>
 )}
 </td>
 <td className="px-4 py-3 text-text-secondary">
 {new Date(t.assignedAt).toLocaleDateString()}
 </td>
 <td className="px-4 py-3 text-right">
 <Button 
 variant="ghost" 
 size="sm" 
 onClick={() => handleRemove(t.orgUnitId, t.isPrimary)}
 disabled={removeMutation.isPending}
 className="text-danger-500 hover:text-danger-600 hover:bg-danger-50"
 >
 <Trash2 className="h-4 w-4" />
 </Button>
 </td>
 </tr>
 ))
 )}
 </tbody>
 </table>
 </div>

 <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
 <DialogContent>
 <DialogHeader>
 <DialogTitle>Assign Territory</DialogTitle>
 </DialogHeader>
 <div className="flex flex-col gap-4 py-4">
 <FormField label="Select Branch / Territory" htmlFor="territoryPicker">
 <OrgUnitPicker
 nodes={(orgUnitsResponse as any) || []}
 value={selectedOrgUnitId}
 onChange={setSelectedOrgUnitId}
 />
 </FormField>
 {assignedOrgUnitIds.has(selectedOrgUnitId) && (
 <em className="text-sm text-danger-500 mt-2">This territory is already assigned to the agent.</em>
 )}
 </div>
 <DialogFooter>
 <Button variant="secondary" onClick={() => setAssignOpen(false)}>Cancel</Button>
 <Button 
 onClick={handleAssign}
 disabled={!selectedOrgUnitId || assignedOrgUnitIds.has(selectedOrgUnitId) || assignMutation.isPending}
 >
 Assign
 </Button>
 </DialogFooter>
 </DialogContent>
 </Dialog>

 <ConfirmDialog
 open={!!removeTarget}
 title="Remove primary territory"
 description="This is the primary territory. Are you sure you want to remove it?"
 confirmLabel="Remove"
 loading={removeMutation.isPending}
 onConfirm={() => {
 if (removeTarget) void removeTerritory(removeTarget.orgUnitId);
 setRemoveTarget(null);
 }}
 onCancel={() => setRemoveTarget(null)}
 />
 </div>
 );
}
